// src/lib/supabase.js
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth helper functions
export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

// Client management functions
export const fetchClients = async () => {
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .order('next_session_date', { ascending: true });  // Sort by created_at in descending order

  if (error) throw error;
  return data;
};

export const fetchClientById = async (id) => {
  const { data, error } = await supabase
    .from('clients')
    .select(`
      *,
      notes (*),
      documents (*)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
};

export const addNewClient = async (clientData) => {
  try {
      const { notes, ...clientInfo } = clientData;
      
      // If no registration number is provided, generate one
      if (!clientInfo.registration_number || clientInfo.registration_number.trim() === '') {
          clientInfo.registration_number = await generateRegistrationNumber();
      }
      
      // First create the client
      const { data: client, error: clientError } = await supabase
          .from('clients')
          .insert([clientInfo])
          .select()
          .single();
  
      if (clientError) throw clientError;
  
      // Then create the note if it exists
      if (notes && notes.trim()) {
        const noteData = {
            client_id: client.id,
            content: notes,
            type: 'session'  // Change this from 'initial' to 'session'
        };
    
        const { error: noteError } = await supabase
            .from('notes')
            .insert([noteData])
            .select();
    
        if (noteError) {
            console.error('Note creation error:', noteError);
            throw noteError;
        }
    }
  
      return client;
  } catch (error) {
      console.error('Full error object:', error);
      throw error;
  }
};

// New function to update a client
export const updateClient = async (clientId, clientData) => {
  try {
    // Extract notes if present
    const { notes, ...clientInfo } = clientData;
    
    // Update the client information
    const { data: client, error: clientError } = await supabase
      .from('clients')
      .update(clientInfo)
      .eq('id', clientId)
      .select()
      .single();

    if (clientError) throw clientError;

    // If notes are provided, add them as a new note
    if (notes && notes.trim()) {
      const noteData = {
        client_id: clientId,
        content: notes,
        type: 'update'
      };

      const { error: noteError } = await supabase
        .from('notes')
        .insert([noteData])
        .select();

      if (noteError) {
        console.error('Note creation error:', noteError);
        throw noteError;
      }
    }

    return client;
  } catch (error) {
    console.error('Update client error:', error);
    throw error;
  }
};

// Document management functions
export const uploadDocument = async (file, clientId, category = 'other') => {
  const fileExt = file.name.split('.').pop();
  
  // Create a unique filename while preserving the original extension
  const fileName = `${clientId}/${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;

  // Ensure we have the correct MIME type for Excel files
  let fileType = file.type;
  
  if (/^(xlsx|xls|xlsm|xlsb|xltx|xltm|xlt)$/i.test(fileExt)) {
    // If it's an Excel file extension but the MIME type is missing or generic
    if (!fileType || fileType === 'application/octet-stream') {
      // Set the appropriate MIME type based on the extension
      fileType = fileExt.toLowerCase() === 'xls' 
        ? 'application/vnd.ms-excel'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
  }

  const { error: uploadError } = await supabase.storage
    .from('client-documents')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: fileType // Use our possibly corrected MIME type
    });

  if (uploadError) throw uploadError;

  // Store the original filename, path, and proper MIME type
  const { data: document, error: dbError } = await supabase
    .from('documents')
    .insert([{
      client_id: clientId,
      name: file.name, // Preserve the original file name exactly as uploaded
      file_path: fileName,
      file_type: fileType, // Use our possibly corrected MIME type
      category: category
    }])
    .select()
    .single();

  if (dbError) throw dbError;

  return document;
};

export const getDocumentUrl = async (path) => {
  // Get a signed URL that will work for downloads
  const { data, error } = await supabase.storage
    .from('client-documents')
    .createSignedUrl(path, 3600); // URL valid for 1 hour

  if (error) throw error;
  
  return data.signedUrl;
};

export const addNote = async (noteData) => {
    const { data, error } = await supabase
      .from('notes')
      .insert([noteData])
      .select()
      .single();
  
    if (error) throw error;
    return data;
  };

export const updateClientStatus = async (clientId, status) => {
  // If status is cancelled, clear the next_session_date
  const updateData = status === 'cancelled' 
    ? { status, next_session_date: null } 
    : { status };

  const { data, error } = await supabase
    .from('clients')
    .update(updateData)
    .eq('id', clientId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteNote = async (noteId) => {
    const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

    if (error) throw error;
};

export const editNote = async (noteId, content) => {
    const { data, error } = await supabase
        .from('notes')
        .update({ content })
        .eq('id', noteId)
        .select()
        .single();

    if (error) throw error;
    return data;
};

export const deleteDocument = async (documentId) => {
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId);
  
    if (error) throw error;
  };

export const deleteClient = async (clientId) => {
  console.log(`Starting deletion process for client ID: ${clientId}`);
  
  try {
    // 1. First check if client exists
    const { data: clientData, error: clientCheckError } = await supabase
      .from('clients')
      .select('id, name')
      .eq('id', clientId)
      .single();
    
    if (clientCheckError) {
      console.error('Error checking client:', clientCheckError);
      throw new Error(`Client check failed: ${clientCheckError.message}`);
    }
    
    if (!clientData) {
      console.error('Client not found:', clientId);
      throw new Error(`Client with ID ${clientId} not found`);
    }
    
    console.log(`Found client: ${clientData.name} (${clientData.id})`);
    
    // Use RPC function to bypass RLS
    const { data: deleteResult, error: deleteError } = await supabase.rpc(
      'admin_delete_client', 
      { p_client_id: clientId }
    );
    
    if (deleteError) {
      console.error('Error in admin_delete_client RPC:', deleteError);
      throw new Error(`Client deletion failed: ${deleteError.message}`);
    }
    
    console.log(`Delete RPC result:`, deleteResult);
    
    // Double-check the client was actually deleted
    const { data: checkData, error: checkError } = await supabase
      .from('clients')
      .select('id')
      .eq('id', clientId);
    
    if (!checkError && checkData && checkData.length > 0) {
      console.error('Client still exists after deletion attempt');
      throw new Error('Deletion reported success but client still exists');
    }
    
    return { success: true, result: deleteResult };
  } catch (error) {
    console.error('Complete error in deleteClient:', error);
    throw error;
  }
};

export const updateClientRegistration = async (clientId, registrationNumber) => {
    const { data, error } = await supabase
      .from('clients')
      .update({ registration_number: registrationNumber })
      .eq('id', clientId)
      .select()
      .single();
  
    if (error) throw error;
    return data;
  };

export const generateRegistrationNumber = async () => {
  // Get current year
  const currentYear = new Date().getFullYear();
  
  // Find the highest registration number in use
  const { data, error } = await supabase
    .from('clients')
    .select('registration_number')
    .not('registration_number', 'is', null)
    .ilike('registration_number', `%NCC${currentYear}`)
    .order('registration_number', { ascending: false })
    .limit(1);
    
  if (error) throw error;
  
  let nextNumber = 1001; // Start with 1000 if no existing registrations
  
  if (data && data.length > 0 && data[0].registration_number) {
    // Try to extract the numeric prefix from the highest existing reg number
    const existingRegNumber = data[0].registration_number;
    const match = existingRegNumber.match(/^(\d+)NCC/);
    
    if (match && match[1]) {
      // If found, increment by 1
      nextNumber = parseInt(match[1], 10) + 1;
    }
  }
  
  // Generate registration number: XXXXNCCYYYY
  const registrationNumber = `${nextNumber}NCC${currentYear}`;
  
  return registrationNumber;
};

export const updateNextSessionDate = async (clientId, nextSessionDate) => {
  const { data, error } = await supabase
    .from('clients')
    .update({ next_session_date: nextSessionDate })
    .eq('id', clientId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Function to fetch sessions for a specific client
export const fetchClientSessions = async (clientId) => {
  const { data, error } = await supabase
    .from('sessions')
    .select('*')
    .eq('client_id', clientId)
    .order('session_date', { ascending: true });

  if (error) throw error;
  return data;
};

// Function to fetch the next upcoming session for a client
export const fetchNextClientSession = async (clientId) => {
  const now = new Date().toISOString();
  
  const { data, error } = await supabase
    .from('sessions')
    .select('*')
    .eq('client_id', clientId)
    .eq('status', 'scheduled') // Changed from 'upcoming' to 'scheduled' to match your schema
    .gte('session_date', now)
    .order('session_date', { ascending: true })
    .limit(1);

  if (error) throw error;
  return data.length > 0 ? data[0] : null;
};

// Function to add a new session
export const addClientSession = async (sessionData) => {
  // Map the data to match your actual database schema
  const adaptedData = {
    client_id: sessionData.client_id,
    session_date: sessionData.session_date,
    status: 'scheduled', // Changed from 'upcoming' to 'scheduled' to match your schema
    session_type: sessionData.title, // Store the title in session_type column
    notes: sessionData.notes || null,
    duration_minutes: 60, // Default duration
    location: 'Online' // Default location
  };
  
  const { data, error } = await supabase
    .from('sessions')
    .insert([adaptedData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Function to update session status
export const updateSessionStatus = async (sessionId, status) => {
  // Make sure the status matches your schema's constraint
  const validStatus = status === 'upcoming' ? 'scheduled' : status;
  
  const { data, error } = await supabase
    .from('sessions')
    .update({ status: validStatus })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Function to delete a session
export const deleteClientSession = async (sessionId) => {
  const { error } = await supabase
    .from('sessions')
    .delete()
    .eq('id', sessionId);

  if (error) throw error;
  return { success: true };
};

// Function to fetch all upcoming sessions
export const fetchAllUpcomingSessions = async () => {
  const now = new Date().toISOString();
  
  const { data, error } = await supabase
    .from('sessions')
    .select('*, clients(name, phone, email)')
    .eq('status', 'scheduled') // Changed from 'upcoming' to 'scheduled' to match your schema
    .gte('session_date', now)
    .order('session_date', { ascending: true });

  if (error) throw error;
  return data;
};

export const updateSession = async (sessionId, sessionData) => {
  const { data, error } = await supabase
    .from('sessions')
    .update({
      session_type: sessionData.session_type,
      session_date: sessionData.session_date,
      duration_minutes: sessionData.duration_minutes,
      location: sessionData.location,
      notes: sessionData.notes
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
};